import {
  bfsMovementRange,
  canMoveAndAttackSameTurn,
  coordEq,
  coordKey,
  effectiveAttackKind,
  isIndirectFire,
  unitAt,
} from "@dailywars/core/engine";
import type { Coord, GameState, Unit } from "@dailywars/core/engine";

import type { AttackRangeFlashIconStyle } from "#/game/attackRangeFlashIcon";
import { captureBarHoverPreview, type CaptureBarHoverPreview } from "#/game/captureBarHoverPreview";
import { getBoardActionHint, type BoardActionHint } from "#/game/selection/actionHint";
import { getBoardCursorMode } from "#/game/selection/cursor";
import { movePreviewFromSelection } from "#/game/selection/movePreviewSelection";
import { computeAttackTileKeysFromPos } from "#/game/selection/options";
import type { BoardCursorMode, SelectionState } from "#/game/selection/types";
import type { StoreState } from "#/game/store/types";

export { pendingAttackFromSelection } from "#/game/selection/pendingAttack";

export {
  canEndTurnFromState,
  canRestartRunFromState,
  canUndoCommittedTurnFromState,
  canUndoFromState,
  canUndoPendingMovesFromState,
  hasUnactedOwnUnitsFromState,
  unactedOwnUnitIdsFromState,
} from "#/game/store/turnSelectors";

export function reachableKeysFromSelection(selection: SelectionState, state: GameState): string[] {
  if (selection.mode === "confirm-capture-reset") {
    return reachableKeysFromSelection(selection.priorSelection, state);
  }
  if (
    selection.mode === "none" ||
    selection.mode === "confirm-attack" ||
    selection.mode === "building" ||
    selection.mode === "enemy-inspect" ||
    selection.mode === "cannon-inspect" ||
    selection.mode === "transport-unload"
  ) {
    if (selection.mode === "transport-unload") {
      return selection.unloadTiles.map((coord) => coordKey(coord));
    }
    return [];
  }
  const unit = state.units.find((u) => u.id === selection.unitId);
  const keys = new Set(
    unit
      ? bfsMovementRange(state, unit).map((coord) => coordKey(coord))
      : selection.reachable.map((coord) => coordKey(coord)),
  );
  for (const join of selection.joinOptions) {
    const target = state.units.find((u) => u.id === join.targetId);
    if (target) keys.add(coordKey(target.pos));
  }
  for (const load of selection.loadOptions) {
    const target = state.units.find((u) => u.id === load.targetId);
    if (target) keys.add(coordKey(target.pos));
  }
  return [...keys];
}

export function attackableTileKeysFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "enemy-inspect" || selection.mode === "cannon-inspect") {
    return selection.attackableTileKeys;
  }
  return attackableCannonKeysFromSelection(selection);
}

export function attackRangeFlashTileKeysFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): string[] {
  if (
    selection.mode !== "unit" &&
    selection.mode !== "move-preview" &&
    selection.mode !== "confirm-capture-reset"
  ) {
    return [];
  }
  const movePreview = movePreviewFromSelection(selection);
  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit || unit.hasAttacked) return [];
  const attackKind = effectiveAttackKind(unit);
  if (!isIndirectFire(attackKind)) return [];
  const fromPos = movePreview ? movePreview.destination : unit.pos;
  return computeAttackTileKeysFromPos({
    state,
    kind: attackKind,
    pos: fromPos,
  });
}

export function attackRangeFlashStyleFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): AttackRangeFlashIconStyle {
  if (
    selection.mode !== "unit" &&
    selection.mode !== "move-preview" &&
    selection.mode !== "confirm-capture-reset"
  ) {
    return "strike";
  }
  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit) return "strike";
  const movePreview = movePreviewFromSelection(selection);
  const previewLeftOrigin = movePreview !== null && !coordEq(movePreview.destination, unit.pos);
  if (!canMoveAndAttackSameTurn(unit.kind) && (previewLeftOrigin || unit.hasMoved)) {
    return "coverage";
  }
  return "strike";
}

export function pathKeysFromSelection(selection: SelectionState): string[] {
  const movePreview = movePreviewFromSelection(selection);
  if (movePreview) {
    return movePreview.path.map((coord) => coordKey(coord));
  }
  if (selection.mode === "confirm-attack") {
    return selection.path.map((coord) => coordKey(coord));
  }
  return [];
}

export function attackableUnitIdsFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "confirm-capture-reset") {
    return attackableUnitIdsFromSelection(selection.priorSelection);
  }
  if (selection.mode === "unit") {
    return selection.attackOptions.flatMap((o) => (o.targetId ? [o.targetId] : []));
  }
  const movePreview = movePreviewFromSelection(selection);
  if (movePreview) {
    return movePreview.attackOptions.flatMap((o) => (o.targetId ? [o.targetId] : []));
  }
  if (selection.mode === "confirm-attack" && selection.targetId) {
    return [selection.targetId];
  }
  return [];
}

function attackableCannonKeysFromSelection(selection: SelectionState): string[] {
  if (selection.mode === "confirm-capture-reset") {
    return attackableCannonKeysFromSelection(selection.priorSelection);
  }
  if (selection.mode === "unit") {
    return selection.attackOptions.flatMap((o) => (o.targetPos ? [coordKey(o.targetPos)] : []));
  }
  const movePreview = movePreviewFromSelection(selection);
  if (movePreview) {
    return movePreview.attackOptions.flatMap((o) => (o.targetPos ? [coordKey(o.targetPos)] : []));
  }
  if (selection.mode === "confirm-attack" && selection.targetKind === "cannon") {
    return [coordKey(selection.targetPos)];
  }
  return [];
}

export function selectedUnitIdFromSelection(selection: SelectionState): string | null {
  if (
    selection.mode === "none" ||
    selection.mode === "building" ||
    selection.mode === "enemy-inspect" ||
    selection.mode === "cannon-inspect"
  )
    return null;
  return selection.unitId;
}

export function previewUnitFromSelection(
  selection: SelectionState,
  units: Unit[],
): { unitId: string; pos: Coord; targetPos?: Coord } | null {
  const movePreview = movePreviewFromSelection(selection);
  if (movePreview) {
    return { unitId: movePreview.unitId, pos: movePreview.destination };
  }
  if (selection.mode === "confirm-attack") {
    const dest =
      selection.path.length > 0
        ? (selection.path[selection.path.length - 1] as Coord)
        : (units.find((u) => u.id === selection.unitId)?.pos ?? null);
    if (!dest) return null;
    return { unitId: selection.unitId, pos: dest, targetPos: selection.targetPos };
  }
  return null;
}

export function inspectedUnitFromState(state: StoreState): Unit | null {
  const { hoveredTile, selection, localState } = state;
  if (!hoveredTile) return null;
  const movePreview = movePreviewFromSelection(selection);
  if (movePreview && coordEq(movePreview.destination, hoveredTile)) {
    return localState.units.find((u) => u.id === movePreview.unitId) ?? null;
  }
  return unitAt(localState, hoveredTile) ?? null;
}

export function boardCursorFromState(state: StoreState, coarsePointer = false): BoardCursorMode {
  return getBoardCursorMode({
    state: state.localState,
    selection: state.selection,
    hoveredTile: state.hoveredTile,
    coarsePointer,
  });
}

export function boardActionHintFromState(
  state: StoreState,
  coarsePointer = false,
): BoardActionHint {
  if (state.localState.currentPlayer !== "p1" || state.localState.status !== "in_progress") {
    return { kind: "none" };
  }
  return getBoardActionHint({
    state: state.localState,
    selection: state.selection,
    hoveredTile: state.hoveredTile,
    coarsePointer,
  });
}

export function captureBarHoverPreviewFromState(state: StoreState): CaptureBarHoverPreview | null {
  return captureBarHoverPreview({
    state: state.localState,
    selection: state.selection,
    hoveredTile: state.hoveredTile,
  });
}
